import { useState, useEffect, useCallback } from 'react';
import { huntService } from '../../features/services/huntService';
import { toLocalYMD } from '../../../utils/dateUtils';

export const HEATMAP_PRESETS = [
  { id: '1y', label: 'Last year', years: 1 },
  { id: '2y', label: 'Last 2 years', years: 2 },
  { id: '3y', label: 'Last 3 years', years: 3 },
  { id: '4y', label: 'Last 4 years', years: 4 },
  { id: '5y', label: 'Last 5 years', years: 5 },
  { id: 'all', label: 'All time', years: null },
];

const DEFAULT_PRESET_ID = '5y';

function getDateRangeForPreset(presetId) {
  const preset = HEATMAP_PRESETS.find((p) => p.id === presetId) || HEATMAP_PRESETS.find((p) => p.id === DEFAULT_PRESET_ID);
  if (!preset || preset.years == null) return { start: null, end: null };
  const end = new Date();
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - preset.years);
  return {
    start: toLocalYMD(start),
    end: toLocalYMD(end),
  };
}

/**
 * @param {'club'|'personal'} mode
 * @param {{ clubId?: number }} options - clubId required when mode === 'club'
 */
export function useDeerSightingsHeatmap(mode, options = {}) {
  const { clubId } = options;
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (mode === 'club' && (clubId == null || clubId === '')) return;
    setLoading(true);
    setError(null);
    try {
      const { start, end } = getDateRangeForPreset(presetId);
      const params = start && end ? { start, end } : {};
      const result =
        mode === 'club'
          ? await huntService.getClubDeerSightingsHeatmap(clubId, params)
          : await huntService.getDeerSightingsHeatmap(params);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load heatmap data');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [mode, clubId, presetId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const dateRange = data?.date_range ?? null;
  const points = Array.isArray(data?.points) ? data.points : [];

  return {
    data,
    dateRange,
    points,
    loading,
    error,
    presetId,
    setPresetId,
    refetch: fetchData,
  };
}
